/**
 * Where the first-run privacy prompt must stay out of the way.
 *
 * The prompt was commented out of both root layouts on 2026-08-15, in the
 * commit that built the skills share funnel and the resume-import handoff into
 * signup, with no note explaining why. The most likely reading is that a consent
 * sheet appearing over that handoff broke the flow it was there to test. So the
 * prompt comes back with a rule instead of a hope: it is suppressed on every
 * route where the person is mid-flow into or out of the app, and shows on the
 * first screen where they have arrived.
 *
 * Prefix matching against the Expo Router pathname, without the group segments
 * (`/login`, not `/(auth)/login`).
 */
const SUPPRESSED_PREFIXES: readonly string[] = [
  // Authentication: a consent sheet under a sign-in form is two decisions at
  // once, and the sheet's own buttons compete with the form's.
  "/login",
  "/register",
  "/auth-step",
  "/forgot-password",
  "/reset-password",
  "/auth-complete",
  "/auth-bootstrap-check",
  "/password-bootstrap",
  "/uae-pass",
  "/auth/uaepass",
  "/oauth/authorize",
  // Handoffs: single-purpose screens that resolve a token and redirect. The
  // person is not staying, and a prompt here is the collision that got the
  // sheet disabled in the first place.
  "/skills-handoff",
  "/resume-import",
  "/cv-handoff",
  "/cv-share",
  "/cv-access",
  "/cv-grant",
  "/cv-email",
  "/jobs-handoff",
  "/messages-handoff",
  "/profile-handoff",
  "/report-handoff",
  "/subscription/return",
  "/extension/connect",
  "/cli/connect",
  "/admin-impersonation",
  // Public share pages reached without an account.
  "/s/",
];

export function shouldSuppressConsentPrompt(pathname: string | null): boolean {
  if (!pathname) return false;
  const path = pathname.toLowerCase();
  return SUPPRESSED_PREFIXES.some(
    (prefix) =>
      path === prefix ||
      path.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`),
  );
}
